// Total calculado de las escalas propias de LogiaCare (esc-phq9, esc-glasgow) dentro del motor genérico de Questionnaire. Un item
// `integer` con la extensión `total-suma-de` (lista de linkId separados por coma) se calcula solo: suma los códigos numéricos de esas
// respuestas. Si falta alguna, NO hay total parcial (un puntaje a medias sería inventar un número). El rango válido sale de las
// extensiones estándar `minValue`/`maxValue`. Un item `choice` con `interpretacion-de` (linkId del total) elige la opción cuyo
// rango ("0-4: ...") contiene el total. Todo puro y sin React — lo usa ItemRenderer.tsx.
import type { Extension, QuestionnaireItem, QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';

export const EXT_TOTAL_SUMA_DE = 'http://logiacare.ec/fhir/StructureDefinition/total-suma-de';
export const EXT_INTERPRETACION_DE = 'http://logiacare.ec/fhir/StructureDefinition/interpretacion-de';
const EXT_MIN = 'http://hl7.org/fhir/StructureDefinition/minValue';
const EXT_MAX = 'http://hl7.org/fhir/StructureDefinition/maxValue';

const extension = (item: QuestionnaireItem, url: string): Extension | undefined => item.extension?.find((e) => e.url === url);

/** linkId de los items que suma este total, o `undefined` si el item no es un total calculado. */
export function fuentesDeTotal(item: QuestionnaireItem): string[] | undefined {
  const lista = extension(item, EXT_TOTAL_SUMA_DE)?.valueString;
  if (!lista) return undefined;
  const ids = lista
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  return ids.length > 0 ? ids : undefined;
}

/** linkId del total del que depende esta interpretación, o `undefined`. */
export function totalDeInterpretacion(item: QuestionnaireItem): string | undefined {
  return extension(item, EXT_INTERPRETACION_DE)?.valueString || undefined;
}

export function rangoDeTotal(item: QuestionnaireItem): { min?: number; max?: number } {
  return { min: extension(item, EXT_MIN)?.valueInteger, max: extension(item, EXT_MAX)?.valueInteger };
}

function buscarItem(items: QuestionnaireResponseItem[] | undefined, linkId: string): QuestionnaireResponseItem | undefined {
  for (const it of items ?? []) {
    if (it.linkId === linkId) return it;
    const hijo = buscarItem(it.item, linkId);
    if (hijo) return hijo;
  }
  return undefined;
}

/** Valor numérico de la respuesta de `linkId` (código numérico de un `choice`, o `valueInteger`); `undefined` si no está respondida. */
export function valorNumericoDe(respuesta: QuestionnaireResponse, linkId: string): number | undefined {
  const a = buscarItem(respuesta.item, linkId)?.answer?.[0];
  if (!a) return undefined;
  if (a.valueInteger !== undefined) return a.valueInteger;
  const codigo = a.valueCoding?.code;
  if (codigo === undefined || codigo.trim() === '') return undefined;
  const n = Number(codigo);
  return Number.isFinite(n) ? n : undefined;
}

/** Suma de las respuestas de `fuentes`; `undefined` si falta responder cualquiera. */
export function sumaDeFuentes(respuesta: QuestionnaireResponse, fuentes: readonly string[]): number | undefined {
  let total = 0;
  for (const f of fuentes) {
    const v = valorNumericoDe(respuesta, f);
    if (v === undefined) return undefined;
    total += v;
  }
  return total;
}

/** `true` si el total es entero y está dentro del rango del item (sin rango declarado, cualquier entero). */
export function totalEnRango(item: QuestionnaireItem, total: number | undefined): boolean {
  if (total === undefined || !Number.isInteger(total)) return false;
  const { min, max } = rangoDeTotal(item);
  return (min === undefined || total >= min) && (max === undefined || total <= max);
}

/** Opción de `item` (choice) cuyo rango numérico inicial del `display` ("13-15: Leve") contiene `total`; `undefined` si ninguna. */
export function opcionDeInterpretacion(item: QuestionnaireItem, total: number | undefined): { code: string; display: string } | undefined {
  if (total === undefined) return undefined;
  for (const op of item.answerOption ?? []) {
    const m = /^\s*(\d+)\s*-\s*(\d+)\b/.exec(op.valueCoding?.display ?? '');
    if (m && total >= Number(m[1]) && total <= Number(m[2]) && op.valueCoding?.code) {
      return { code: op.valueCoding.code, display: op.valueCoding.display ?? op.valueCoding.code };
    }
  }
  return undefined;
}
